import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Breadcrumb } from '../../../../components/Breadcrumb';
import { Calendar } from '../../../../components/calendar/Calendar';
import { buildMonthGrid, monthOf, todayCalendarDate } from '../../../../components/calendar/monthGrid';
import { ErrorState } from '../../../../components/ErrorState';
import { Tooltip } from '../../../../components/Tooltip';
import { ApiError } from '../../../../lib/apiError';
import { formatCalendarMonth } from '../../../../lib/formatDate';
import { maintenanceItemsApi } from '../buildingManagementApi';
import type { MaintenanceCalendarEntry } from '../buildingManagementTypes';
import { MaintenanceCalendarLegend } from '../components/MaintenanceCalendarLegend';
import { entryDot, entryState } from '../components/maintenanceCalendarDots';
import { maintenanceItemTitle } from '../utils/maintenanceItemTitle';

/**
 * Building Management's calendar — every item's schedule, a month at a time.
 *
 * **Two kinds of entry, told apart on sight and in words.** An item's CURRENT due date is a stored
 * fact and carries its aging band; every later occurrence is the recurrence projected forward
 * and reads On Schedule. Projections run from the due date as it stands, so recording a
 * maintenance moves the whole run.
 *
 * **The month is fetched as the GRID shows it** — borrowed days included — so an entry on a
 * leading or trailing day of the grid is never a blank cell the reader has to page to fill.
 *
 * The register's own Calendar view is the YEAR; this page is the month. Both read one legend and
 * one set of dots, so the two cannot disagree about what a colour means.
 */
export function MaintenanceCalendarPage() {
  const navigate = useNavigate();

  const [month, setMonth] = useState(() => monthOf(todayCalendarDate()));
  const [entries, setEntries] = useState<MaintenanceCalendarEntry[] | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const range = useMemo(() => {
    const cells = buildMonthGrid(month);
    return { from: cells[0]?.date ?? `${month}-01`, to: cells[cells.length - 1]?.date ?? `${month}-28` };
  }, [month]);

  useEffect(() => {
    const controller = new AbortController();

    // The previous month's entries are dropped first, so a slow answer never paints them on the new grid.
    setEntries(null);
    setError(null);

    maintenanceItemsApi
      .calendar(range.from, range.to, {}, controller.signal)
      .then((next) => {
        if (!controller.signal.aborted) setEntries(next);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setError(caught instanceof ApiError ? caught : new ApiError('Could not load the calendar.', 0));
      });

    return () => controller.abort();
  }, [range, reloadKey]);

  const byDate = useMemo(() => {
    const map = new Map<string, MaintenanceCalendarEntry[]>();

    for (const entry of entries ?? []) {
      const list = map.get(entry.date) ?? [];
      list.push(entry);
      map.set(entry.date, list);
    }

    return map;
  }, [entries]);

  const renderDay = useCallback(
    (date: string) => {
      const day = byDate.get(date);
      if (!day) return null;

      return (
        <ul className="flex min-w-0 flex-col gap-0.5">
          {day.map((entry) => {
            const state = entryState(entry);
            const title = maintenanceItemTitle(entry);

            return (
              <li key={`${entry.maintenanceItemId}-${entry.date}`} className="min-w-0">
                <Tooltip label={`${entry.referenceNumber} — ${title} · ${state}`} block>
                  <button
                    type="button"
                    onClick={() => navigate(`/general-services/building-management/maintenance/${entry.maintenanceItemId}`)}
                    // A calendar cell's value is never the dark slate-800/900 — slate-500, regular
                    // weight, 11px; hover underlines it (product call, 12 Oct 2026).
                    className="flex w-full min-w-0 items-center gap-1.5 rounded px-1 py-0.5 text-left text-[11px] text-slate-500 hover:bg-slate-100 hover:underline"
                  >
                    <span aria-hidden="true" className={`h-2 w-2 shrink-0 rounded-full ${entryDot(entry)}`} />
                    <span className="truncate">{title}</span>
                    <span className="sr-only">
                      , {entry.referenceNumber}, {state}
                    </span>
                  </button>
                </Tooltip>
              </li>
            );
          })}
        </ul>
      );
    },
    [byDate, navigate],
  );

  const count = entries?.filter((e) => e.date.startsWith(month)).length ?? 0;

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-50">
      <Breadcrumb segments={['General Services', 'Building Management', 'Calendar']} />

      <div className="modern-scrollbar min-h-0 flex-1 overflow-y-auto overflow-x-hidden p-4">
        <div className="flex flex-col gap-3">
          {/* What the month holds, in one line — and the legend, so a dot is never a fact only a sighted reader gets. */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-slate-500" aria-live="polite">
              {entries === null && !error
                ? `Loading ${formatCalendarMonth(month)}…`
                : `${count} ${count === 1 ? 'item falls' : 'items fall'} due in ${formatCalendarMonth(month)}.`}
            </p>

            <MaintenanceCalendarLegend />
          </div>

          {error ? (
            <ErrorState message={error.message} onRetry={() => setReloadKey((k) => k + 1)} />
          ) : (
            <Calendar size="page" month={month} onMonthChange={setMonth} renderDay={renderDay} />
          )}
        </div>
      </div>
    </div>
  );
}
